/**
 * Public transit preference sections for the transports MiniModal.
 */

import React, { useCallback, useMemo } from "react";
import {
    transitPrefsSlice,
    usePersistedSlice,
    type MaxTransfersPreference,
    type TransitModeFilter,
} from "../../../../../store/persistence";
import { TRANSIT_MODE_UI_OPTIONS } from "../../../../../tools/transitPrefs.tools";
import ImageOptionSelector from "../../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../../MiniModal/MiniModalSection";
import MiniModalSelect, { type MiniModalSelectOption } from "../../../../MiniModal/MiniModalSelect";
import { TransferCountDecoration } from "../../../../MiniModal/miniModalSelectTransferDecoration";

/**
 * Builds transfer-preference options for {@link MiniModalSelect}.
 *
 * @param isDarkUi Theme flag forwarded to row decorations.
 * @returns Select options wired to persisted `maxTransfers` values.
 */
const buildMaxTransfersSelectOptions = ( isDarkUi: boolean ): readonly MiniModalSelectOption[] => {
    return [
        {
            "id":         "0",
            "label":      "Direct",
            "sublabel":   "Sans changement",
            "decoration": <TransferCountDecoration
                isDarkUi={isDarkUi}
                maxTransfers={0}
                          />,
        },
        {
            "id":         "1",
            "label":      "1 correspondance",
            "sublabel":   "Au plus un changement",
            "decoration": <TransferCountDecoration
                isDarkUi={isDarkUi}
                maxTransfers={1}
                          />,
        },
        {
            "id":         "2",
            "label":      "2 correspondances",
            "sublabel":   "Jusqu'à deux changements",
            "decoration": <TransferCountDecoration
                isDarkUi={isDarkUi}
                maxTransfers={2}
                          />,
        },
        {
            "id":         "3",
            "label":      "3 correspondances",
            "sublabel":   "Jusqu'à trois changements",
            "decoration": <TransferCountDecoration
                isDarkUi={isDarkUi}
                maxTransfers={3}
                          />,
        },
        {
            "id":         "20",
            "label":      "Peu importe",
            "sublabel":   "Aucune limite",
            "decoration": <TransferCountDecoration
                isDarkUi={isDarkUi}
                maxTransfers={null}
                          />,
        },
    ];
};

/** Props for {@link TransitPrefsModalContent}. */
export interface TransitPrefsModalContentProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Transit filter sections rendered inside the transports MiniModal transit tab.
 *
 * @param props Component props. See `TransitPrefsModalContentProps`.
 * @returns Transit mode and transfer-preference controls.
 */
const TransitPrefsModalContent: React.FC<TransitPrefsModalContentProps> = ( { isDarkUi } ) => {
    const [ transitPrefs, setTransitPrefs ] = usePersistedSlice( transitPrefsSlice );

    const transitModeSelection = useMemo<readonly string[]>( () => {
        return transitPrefs.enabledModes;
    }, [ transitPrefs.enabledModes ] );

    const maxTransfersSelection = useMemo<string>( () => {
        return String( transitPrefs.maxTransfers );
    }, [ transitPrefs.maxTransfers ] );

    const maxTransfersSelectOptions = useMemo(
        () => buildMaxTransfersSelectOptions( isDarkUi ),
        [ isDarkUi ],
    );

    const handleTransitModeChange = useCallback( ( nextValue: readonly string[] ): void => {
        if ( nextValue.length === 0 ) {
            return;
        }

        setTransitPrefs( {
            ...transitPrefs,
            "enabledModes": nextValue as TransitModeFilter[],
        } );
    }, [ transitPrefs, setTransitPrefs ] );

    const handleMaxTransfersChange = useCallback( ( nextValue: string ): void => {
        const parsed = parseInt( nextValue, 10 );
        if ( Number.isNaN( parsed ) ) {
            return;
        }

        const allowedValues: readonly MaxTransfersPreference[] = [ 0, 1, 2, 3, 20 ];
        if ( !allowedValues.includes( parsed as MaxTransfersPreference ) ) {
            return;
        }

        setTransitPrefs( {
            ...transitPrefs,
            "maxTransfers": parsed as MaxTransfersPreference,
        } );
    }, [ transitPrefs, setTransitPrefs ] );

    return (
        <>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Correspondances"
            >
                <MiniModalSelect
                    ariaLabel="Nombre maximum de correspondances"
                    isDarkUi={isDarkUi}
                    onChange={handleMaxTransfersChange}
                    options={maxTransfersSelectOptions}
                    value={maxTransfersSelection}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Modes"
            >
                <ImageOptionSelector
                    columns={3}
                    isDarkUi={isDarkUi}
                    onChange={handleTransitModeChange}
                    options={TRANSIT_MODE_UI_OPTIONS}
                    selectionType="checkbox"
                    value={transitModeSelection}
                />
            </MiniModalSection>
        </>
    );
};

export default TransitPrefsModalContent;
